import React, { useState, useEffect } from 'react';
import { Download, Copy, Check, Sparkles, Settings, Share2, History, Palette, Shield, Maximize2, Type, Link2, Mail, Phone, Wifi, MessageSquare, Coffee, Heart, CreditCard, QrCode, ChevronRight, Zap, FileDown, Globe, Layers, RefreshCw, Users, Award, TrendingUp, ArrowRight } from 'lucide-react';

const AMQRCodeStudio = () => {
  const [text, setText] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('text');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [useGradient, setUseGradient] = useState(false);
  
  const [settings, setSettings] = useState({
    size: 300,
    bgColor: 'ffffff',
    fgColor: '4169E1',
    errorCorrection: 'M',
    format: 'png'
  });

  const colorPresets = [
    { name: 'AM Blue', color: '4169E1' },
    { name: 'Black', color: '000000' },
    { name: 'Purple', color: '8B4F9F' },
    { name: 'Green', color: '10B981' },
    { name: 'Red', color: 'EF4444' },
    { name: 'Orange', color: 'F97316' }
  ];

  const gradientPresets = [
    { name: 'Ocean', colors: ['4169E1', '06B6D4'] },
    { name: 'Sunset', colors: ['F97316', 'EC4899'] },
    { name: 'Purple Dream', colors: ['8B4F9F', 'EC4899'] },
    { name: 'Forest', colors: ['10B981', '059669'] },
    { name: 'Fire', colors: ['EF4444', 'F97316'] },
    { name: 'Royal', colors: ['4169E1', '7C3AED'] }
  ];

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    
    document.title = 'AM QR Studio - Free QR Code Generator | Create Custom QR Codes Instantly';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = 'Create professional QR codes instantly with AM QR Studio. Free QR code generator for URLs, WiFi, business cards, and more. No registration required. Download in PNG, SVG, or JPEG.';
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.content = 'QR code generator, free QR code, custom QR codes, business card QR code, WiFi QR code, URL QR code, AM CARDMATE, digital business cards, QR code maker';
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.content = 'AM QR Studio - Free Professional QR Code Generator';
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.content = 'Create unlimited professional QR codes for free. Perfect for digital business cards, WiFi networks, URLs, and more. No registration required.';
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.content = 'https://qrstudio.amcardmate.com';
    }
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.content = 'https://qrstudio.amcardmate.com/og-image.png';
    }
    
    const twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (twitterCard) {
      twitterCard.content = 'summary_large_image';
    }
    
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.content = 'AM QR Studio - Free Professional QR Code Generator';
    }
    
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.content = 'Create unlimited professional QR codes for free. Perfect for digital business cards, WiFi networks, URLs, and more.';
    }
    
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.content = 'https://qrstudio.amcardmate.com/og-image.png';
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = 'https://qrstudio.amcardmate.com';
    }
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "AM QR Studio",
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "All",
      "url": "https://qrstudio.amcardmate.com",
      "description": "Professional QR code generator for creating custom QR codes instantly. Free tool for URLs, WiFi, business cards, and more.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "creator": {
        "@type": "Organization",
        "name": "AM CARDMATE",
        "url": "https://amcardmate.com"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "5432"
      }
    };
    
    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const [quickInputs, setQuickInputs] = useState({
    url: '',
    email: '',
    phone: '',
    wifi: { ssid: '', password: '', security: 'WPA' },
    sms: { phone: '', message: '' }
  });

  const inputTypes = [
    { id: 'text', label: 'Text', icon: Type },
    { id: 'url', label: 'URL', icon: Link2 },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'phone', label: 'Phone', icon: Phone },
    { id: 'wifi', label: 'WiFi', icon: Wifi },
    { id: 'sms', label: 'SMS', icon: MessageSquare }
  ];

  const generateQRCode = (value, customSettings = settings) => {
    if (!value.trim()) {
      setQrCode('');
      return;
    }
    
    setIsGenerating(true);
    const { size, bgColor, fgColor, errorCorrection } = customSettings;
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=${bgColor}&color=${fgColor}&ecc=${errorCorrection}&format=svg`;
    setQrCode(qrApiUrl);
    
    const newEntry = {
      id: Date.now(),
      text: value,
      timestamp: new Date().toLocaleString(),
      settings: customSettings
    };
    setHistory(prev => [newEntry, ...prev.slice(0, 9)]);
    
    setTimeout(() => {
      setIsGenerating(false);
    }, 300);
  };

  const getFormattedInput = () => {
    switch(activeTab) {
      case 'url':
        return quickInputs.url.startsWith('http') ? quickInputs.url : `https://${quickInputs.url}`;
      case 'email':
        return `mailto:${quickInputs.email}`;
      case 'phone':
        return `tel:${quickInputs.phone}`;
      case 'wifi':
        return `WIFI:T:${quickInputs.wifi.security};S:${quickInputs.wifi.ssid};P:${quickInputs.wifi.password};;`;
      case 'sms':
        return `sms:${quickInputs.sms.phone}?body=${encodeURIComponent(quickInputs.sms.message)}`;
      default:
        return text;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = getFormattedInput();
      generateQRCode(input);
    }, 500);

    return () => clearTimeout(timer);
  }, [text, quickInputs, settings, activeTab]);

  const downloadQRCode = async () => {
    if (!qrCode) return;
    
    try {
      const downloadUrl = qrCode.replace('format=svg', `format=${settings.format}`);
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `am-qrcode-${Date.now()}.${settings.format}`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const shareQRCode = async () => {
    if (!qrCode) return;
    
    try {
      const response = await fetch(qrCode.replace('format=svg', 'format=png'));
      const blob = await response.blob();
      const file = new File([blob], 'am-qrcode.png', { type: 'image/png' });
      
      if (navigator.share) {
        await navigator.share({
          files: [file],
          title: 'AM QR Code',
          text: 'Check out this QR code from AM CARDMATE!'
        });
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getFormattedInput());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadFromHistory = (entry) => {
    setText(entry.text);
    setSettings(entry.settings);
    setShowHistory(false);
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">QR</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  AM <span className="text-blue-600">QR</span>STUDIO
                </h1>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="https://amcardmate.com" className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-all hover:shadow-lg font-medium flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Main Website
              </a>
            </nav>
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex items-center gap-1">
              <span className="text-yellow-400 animate-pulse">★</span>
              <span className="text-sm font-medium text-blue-600">Next Generation Digital Tools</span>
            </div>
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-2">
            QR codes get scanned.
          </h2>
          <h2 className="text-4xl md:text-6xl font-black mb-6">
            <span className="text-blue-600">AM QR</span>
            <span className="text-purple-600">STUDIO</span>
            <span className="text-gray-900"> works.</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create, customize, and share professional QR codes instantly. Perfect for digital business cards, 
            WiFi networks, URLs, and more - with advanced features and beautiful designs.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="https://amcardmate.com" className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl hover:bg-blue-700 transition-all hover:shadow-xl font-semibold flex items-center gap-2 transform hover:-translate-y-0.5">
              <Globe className="w-5 h-5" />
              Go to Main Website
            </a>
            <button 
              onClick={() => {
                document.getElementById('create').scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-gray-700 px-8 py-3.5 rounded-2xl hover:bg-gray-50 transition-all border-2 border-gray-200 font-semibold hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Start Creating
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8 mb-16">
        <div className="flex flex-wrap gap-4 justify-center">
          <div className="bg-white rounded-2xl shadow-lg px-5 py-3 flex items-center gap-3 border border-gray-100">
            <div className="w-9 h-9 bg-green-500 rounded-xl flex items-center justify-center shadow-md">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">QR Code Ready</p>
              <p className="text-xs text-gray-500">Start creating instantly</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg px-5 py-3 flex items-center gap-3 border border-gray-100">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">New Features</p>
              <p className="text-xs text-gray-500">6 QR code types available</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto" id="create">
          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div>
            <div className="relative p-6 md:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                Create Your QR Code
              </h3>

              <div className="flex flex-wrap gap-2 mb-6">
                {inputTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setActiveTab(type.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all text-sm ${
                      activeTab === type.id 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-blue-600'
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{type.label}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {activeTab === 'text' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Enter any text
                    </label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type anything here..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                      rows="4"
                    />
                  </div>
                )}

                {activeTab === 'url' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={quickInputs.url}
                      onChange={(e) => setQuickInputs({...quickInputs, url: e.target.value})}
                      placeholder="example.com"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {activeTab === 'email' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={quickInputs.email}
                      onChange={(e) => setQuickInputs({...quickInputs, email: e.target.value})}
                      placeholder="hello@example.com"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {activeTab === 'phone' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={quickInputs.phone}
                      onChange={(e) => setQuickInputs({...quickInputs, phone: e.target.value})}
                      placeholder="+1234567890"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {activeTab === 'wifi' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Network Name (SSID)
                      </label>
                      <input
                        type="text"
                        value={quickInputs.wifi.ssid}
                        onChange={(e) => setQuickInputs({...quickInputs, wifi: {...quickInputs.wifi, ssid: e.target.value}})}
                        placeholder="MyWiFiNetwork"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={quickInputs.wifi.password}
                        onChange={(e) => setQuickInputs({...quickInputs, wifi: {...quickInputs.wifi, password: e.target.value}})}
                        placeholder="••••••••"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Security Type
                      </label>
                      <select
                        value={quickInputs.wifi.security}
                        onChange={(e) => setQuickInputs({...quickInputs, wifi: {...quickInputs.wifi, security: e.target.value}})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="WPA">WPA/WPA2</option>
                        <option value="WEP">WEP</option>
                        <option value="nopass">None</option>
                      </select>
                    </div>
                  </div>
                )}

                {activeTab === 'sms' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={quickInputs.sms.phone}
                        onChange={(e) => setQuickInputs({...quickInputs, sms: {...quickInputs.sms, phone: e.target.value}})}
                        placeholder="+1234567890"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Message
                      </label>
                      <textarea
                        value={quickInputs.sms.message}
                        onChange={(e) => setQuickInputs({...quickInputs, sms: {...quickInputs.sms, message: e.target.value}})}
                        placeholder="Your message here..."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        rows="3"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium transition-all group"
                >
                  <Settings className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                  Advanced Settings
                  <ChevronRight className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-90' : ''}`} />
                </button>
                
                {showSettings && (
                  <div className="mt-4 space-y-4 p-5 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                        <Maximize2 className="w-4 h-4 text-blue-600" />
                        Size: {settings.size}px
                      </label>
                      <input
                        type="range"
                        min="100"
                        max="500"
                        value={settings.size}
                        onChange={(e) => setSettings({...settings, size: e.target.value})}
                        className="w-full accent-blue-600"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-3 flex items-center gap-2">
                        <Palette className="w-4 h-4 text-purple-600" />
                        QR Code Color
                      </label>
                      
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-2">Quick Colors</p>
                        <div className="flex flex-wrap gap-2">
                          {colorPresets.map(preset => (
                            <button
                              key={preset.name}
                              onClick={() => setSettings({...settings, fgColor: preset.color})}
                              className="group relative"
                              title={preset.name}
                            >
                              <div 
                                className={`w-10 h-10 rounded-lg shadow-sm border-2 transition-all ${
                                  settings.fgColor === preset.color ? 'border-blue-500 scale-110' : 'border-gray-200 hover:border-gray-300'
                                }`}
                                style={{ backgroundColor: `#${preset.color}` }}
                              />
                              {settings.fgColor === preset.color && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Check className="w-5 h-5 text-white drop-shadow" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Custom QR Color</label>
                          <input
                            type="color"
                            value={`#${settings.fgColor}`}
                            onChange={(e) => setSettings({...settings, fgColor: e.target.value.slice(1)})}
                            className="w-full h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Background Color</label>
                          <input
                            type="color"
                            value={`#${settings.bgColor}`}
                            onChange={(e) => setSettings({...settings, bgColor: e.target.value.slice(1)})}
                            className="w-full h-10 rounded-lg border-2 border-gray-200 cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        Error Correction Level
                      </label>
                      <select
                        value={settings.errorCorrection}
                        onChange={(e) => setSettings({...settings, errorCorrection: e.target.value})}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="L">Low (7%)</option>
                        <option value="M">Medium (15%)</option>
                        <option value="Q">Quartile (25%)</option>
                        <option value="H">High (30%)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Download Format
                      </label>
                      <select
                        value={settings.format}
                        onChange={(e) => setSettings({...settings, format: e.target.value})}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="png">PNG</option>
                        <option value="svg">SVG</option>
                        <option value="jpg">JPEG</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl">
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
            <div className="relative p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                  Your QR Code
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                    title="History"
                  >
                    <History className="w-5 h-5" />
                  </button>
                  {getFormattedInput() && (
                    <button
                      onClick={copyToClipboard}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                      title="Copy input"
                    >
                      {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                    </button>
                  )}
                </div>
              </div>

              <div className={`relative flex items-center justify-center min-h-[300px] transition-all duration-500 ${isGenerating ? 'scale-95 opacity-50' : 'scale-100 opacity-100'}`}>
                {qrCode ? (
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
                    <div className="relative bg-white p-8 rounded-2xl shadow-2xl border-2 border-gray-100 transform group-hover:scale-105 transition-transform duration-300">
                      <img
                        src={qrCode}
                        alt="QR Code"
                        style={{ width: `${settings.size}px`, height: `${settings.size}px`, maxWidth: '100%', height: 'auto' }}
                        className="mx-auto"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-64 h-64 max-w-full border-4 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                      <div>
                        <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500 px-4">Start typing to generate QR code</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {qrCode && (
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={downloadQRCode}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-medium flex items-center justify-center gap-2 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                  >
                    <Download className="w-5 h-5" />
                    Download {settings.format.toUpperCase()}
                  </button>
                  {navigator.share && (
                    <button
                      onClick={shareQRCode}
                      className="px-6 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 rounded-xl font-medium flex items-center gap-2 hover:from-gray-100 hover:to-gray-200 transform hover:scale-105 transition-all duration-200 border border-gray-200"
                    >
                      <Share2 className="w-5 h-5" />
                      Share
                    </button>
                  )}
                </div>
              )}

              {showHistory && history.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <History className="w-4 h-4 text-blue-600" />
                    Recent QR Codes
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {history.map(entry => (
                      <button
                        key={entry.id}
                        onClick={() => loadFromHistory(entry)}
                        className="w-full text-left p-3 bg-white rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all border border-gray-200"
                      >
                        <p className="text-sm text-gray-800 truncate font-medium">{entry.text}</p>
                        <p className="text-xs text-gray-500">{entry.timestamp}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How <span className="text-blue-600">AM QR Studio</span> Works
          </h3>
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 p-8 lg:p-12 flex items-center justify-center order-2 lg:order-1">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1613909207039-6b173b755cc1?w=800&h=800&fit=crop" 
                    alt="QR Code scanning demonstration"
                    className="relative w-full max-w-md rounded-2xl shadow-xl transform group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
              
              <div className="p-8 lg:p-12 flex items-center order-1 lg:order-2">
                <div className="space-y-8 w-full">
                  <div className="flex gap-4 items-start">
                    <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center flex-shrink-0 text-xl font-bold shadow-lg">
                      1
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-bold text-xl text-gray-900 mb-2">Choose Type</h4>
                      <p className="text-gray-600 leading-relaxed">Select from 6 different QR code types for your needs</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="w-14 h-14 bg-purple-600 text-white rounded-2xl flex items-center justify-center flex-shrink-0 text-xl font-bold shadow-lg">
                      2
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-bold text-xl text-gray-900 mb-2">Customize</h4>
                      <p className="text-gray-600 leading-relaxed">Add colors, adjust size, and set error correction</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="w-14 h-14 bg-pink-600 text-white rounded-2xl flex items-center justify-center flex-shrink-0 text-xl font-bold shadow-lg">
                      3
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-bold text-xl text-gray-900 mb-2">Download</h4>
                      <p className="text-gray-600 leading-relaxed">Get your QR code in PNG, SVG, or JPEG format</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-1 max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl p-8 md:p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-left">
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Transform Your <span className="text-blue-600">Networking</span>
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Say goodbye to manual contact exchanges. With AM QR Studio, share your complete digital business card, portfolio, or social profiles with a single scan. Perfect for conferences, meetings, and events.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900 mb-1">Professional Networking</h4>
                        <p className="text-gray-600 text-sm">Share contact info instantly at business events</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Award className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900 mb-1">Marketing Campaigns</h4>
                        <p className="text-gray-600 text-sm">Track engagement with dynamic QR codes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <TrendingUp className="w-5 h-5 text-pink-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900 mb-1">Event Management</h4>
                        <p className="text-gray-600 text-sm">Streamline check-ins and information sharing</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-2xl opacity-20"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop" 
                    alt="Professional networking"
                    className="relative rounded-2xl shadow-2xl w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20" id="features">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Why Choose <span className="text-blue-600">AM QR Studio</span>?
          </h3>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Professional QR code generation with advanced features and beautiful designs
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center group h-full">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-1 max-w-xs mx-auto transform group-hover:scale-105 transition-transform duration-300 h-full">
                <div className="bg-white rounded-xl p-8 h-full flex flex-col">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-gray-900">Instant Generation</h4>
                  <p className="text-gray-600 flex-1">Create QR codes in real-time as you type, with no delays or waiting</p>
                </div>
              </div>
            </div>
            <div className="text-center group h-full">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-1 max-w-xs mx-auto transform group-hover:scale-105 transition-transform duration-300 h-full">
                <div className="bg-white rounded-xl p-8 h-full flex flex-col">
                  <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-purple-600" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-gray-900">High Quality</h4>
                  <p className="text-gray-600 flex-1">Generate high-resolution QR codes with adjustable error correction levels</p>
                </div>
              </div>
            </div>
            <div className="text-center group h-full">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-1 max-w-xs mx-auto transform group-hover:scale-105 transition-transform duration-300 h-full">
                <div className="bg-white rounded-xl p-8 h-full flex flex-col">
                  <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <FileDown className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-gray-900">Multiple Formats</h4>
                  <p className="text-gray-600 flex-1">Download your QR codes in PNG, SVG, or JPEG formats for any use case</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1600&h=600&fit=crop" 
                alt="Business success"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-purple-900/90"></div>
            </div>
            <div className="relative p-12 md:p-16 text-white">
              <h3 className="text-3xl font-bold mb-2 text-center">Trusted by Thousands</h3>
              <p className="text-blue-100 text-center mb-12 max-w-2xl mx-auto">
                Join thousands of professionals who create QR codes with AM QR Studio every day
              </p>
              <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">10K+</div>
                  <div className="text-blue-200 font-medium">QR Codes Generated</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">5K+</div>
                  <div className="text-blue-200 font-medium">Happy Users</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">100%</div>
                  <div className="text-blue-200 font-medium">Free Forever</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">24/7</div>
                  <div className="text-blue-200 font-medium">Always Available</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Perfect for <span className="text-blue-600">Digital Business Cards</span>
          </h3>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Integrate QR codes seamlessly with your AM CARDMATE digital business cards for instant contact sharing
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-6">
              <div className="w-20 h-20 bg-white rounded-xl shadow-lg mx-auto mb-4 flex items-center justify-center">
                <Link2 className="w-10 h-10 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2 text-center">Quick Links</h4>
              <p className="text-gray-600 text-sm text-center">Share your portfolio, LinkedIn, or website instantly</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-6">
              <div className="w-20 h-20 bg-white rounded-xl shadow-lg mx-auto mb-4 flex items-center justify-center">
                <Phone className="w-10 h-10 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2 text-center">Contact Info</h4>
              <p className="text-gray-600 text-sm text-center">One scan to save all your contact details</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-6">
              <div className="w-20 h-20 bg-white rounded-xl shadow-lg mx-auto mb-4 flex items-center justify-center">
                <Wifi className="w-10 h-10 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2 text-center">WiFi Access</h4>
              <p className="text-gray-600 text-sm text-center">Share network credentials securely with guests</p>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 md:p-12 max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 text-left">
                <div className="mb-6">
                  <div className="flex gap-1 mb-4 justify-start">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xl">★</span>
                    ))}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-left">
                    "AM QR Studio transformed how we handle event networking"
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6 text-left">
                    We used to struggle with business card exchanges at our annual conference. Now, attendees simply scan QR codes to connect instantly. The customization options let us match our brand perfectly, and the analytics help us measure engagement.
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      SJ
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-600">Event Manager, TechConf</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <img 
                  src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop" 
                  alt="Conference networking"
                  className="rounded-2xl shadow-xl w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <QrCode className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-600">Advanced Features</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Take Your QR Codes <span className="text-blue-600">Further</span>
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlock the full potential of QR codes with our advanced features
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Layers className="w-10 h-10" />
              </div>
              <h4 className="font-semibold text-xl text-gray-900 mb-2">Bulk Generation</h4>
              <p className="text-gray-600">Create multiple QR codes at once for events, products, or marketing campaigns</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-10 h-10" />
              </div>
              <h4 className="font-semibold text-xl text-gray-900 mb-2">Dynamic QR Codes</h4>
              <p className="text-gray-600">Update your QR code destination without reprinting - perfect for campaigns</p>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 via-orange-50 to-pink-50 rounded-3xl p-10 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-300/20 to-pink-300/20 rounded-full blur-3xl"></div>
            <div className="relative">
              <Coffee className="w-14 h-14 text-amber-600 mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Love AM QR Studio?</h3>
              <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                If you find our QR code generator helpful, consider supporting us! Your contribution helps us maintain and improve this free tool.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://buymeacoffee.com/alinamakov7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium py-3 px-6 rounded-xl transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  <Coffee className="w-5 h-5" />
                  Buy Me a Coffee
                </a>
                <a
                  href="https://paypal.me/amcardmate?country.x=GB&locale.x=en_GB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 font-medium py-3 px-6 rounded-xl transform hover:scale-105 transition-all duration-200 shadow-lg border border-gray-200"
                >
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  PayPal
                </a>
              </div>
              <p className="text-sm text-gray-600 mt-6">
                Every contribution makes a difference! Thank you for your support ❤️
              </p>
            </div>
          </div>
        </div>

        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl"></div>
          <div className="relative bg-gradient-to-r from-blue-600/95 to-purple-600/95 rounded-3xl p-12 text-center text-white backdrop-blur-sm">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Networking?
              </h3>
              <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                Join thousands of professionals using QR codes with digital business cards for seamless connections
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a href="https://amcardmate.com/app-cards" className="inline-flex items-center gap-2 bg-white text-blue-600 font-medium py-3 px-8 rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-lg">
                  Try AM CARDMATE
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="https://amcardmate.com/nfc-cards" className="inline-flex items-center gap-2 bg-blue-700/50 backdrop-blur-sm text-white font-medium py-3 px-8 rounded-xl hover:bg-blue-700/70 transform hover:scale-105 transition-all duration-200 border border-blue-400/30">
                  Explore NFC Cards
                </a>
              </div>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold mb-2">Free Forever</div>
                  <p className="text-blue-200">No hidden fees or premium plans</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">Unlimited QR Codes</div>
                  <p className="text-blue-200">Generate as many as you need</p>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2">No Registration</div>
                  <p className="text-blue-200">Start creating immediately</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 mt-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12 text-center md:text-left">
            <div>
              <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
                <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg">QR</span>
                </div>
                <span className="font-bold text-xl">AM QR Studio</span>
              </div>
              <p className="text-blue-200 text-sm">
                Professional QR code generator by AM CARDMATE. Create unlimited QR codes for free.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-blue-100">Products</h4>
              <ul className="space-y-2">
                <li><a href="https://amcardmate.com/app-cards" className="text-blue-200 hover:text-white transition-colors">App Cards</a></li>
                <li><a href="https://amcardmate.com/nfc-cards" className="text-blue-200 hover:text-white transition-colors">NFC Cards</a></li>
                <li><a href="https://amcardmate.com/enterprise" className="text-blue-200 hover:text-white transition-colors">Enterprise</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-blue-100">Company</h4>
              <ul className="space-y-2">
                <li><a href="https://amcardmate.com/about" className="text-blue-200 hover:text-white transition-colors">About</a></li>
                <li><a href="https://amcardmate.com/blog" className="text-blue-200 hover:text-white transition-colors">Blog</a></li>
                <li><a href="https://amcardmate.com/contact" className="text-blue-200 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-blue-100">Support</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-blue-200 hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-blue-800/50 pt-8 text-center">
            <p className="text-blue-200">
              © 2025 AM CARDMATE. All rights reserved. | <a href="https://amcardmate.com" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">Visit Main Site</a>
            </p>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://buymeacoffee.com/alinamakov7"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative"
        >
          <div className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transform hover:scale-110 transition-all duration-300 animate-pulse hover:animate-none">
            <Coffee className="w-7 h-7 text-white" />
          </div>
          
          <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className="bg-gray-900 text-white text-sm py-2 px-3 rounded-lg whitespace-nowrap shadow-lg">
              Buy me a coffee ☕
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

export default AMQRCodeStudio;